import { useCallback, useContext, useEffect, useState } from "react"

// Context
import { UserContext } from "../../../../../context/userContext"

//Referrals
import { refApiReferralEntityCheckName } from "../../../partnerAPI"

//Styles
import styles from "../../../../../styles/referrals/ReferralEntityFormNameCheck.module.css"

//PrimeReact
import ReferralEntityFormValidationDialog from "./ReferralEntityFormValidationDialog"
import { useField } from "formik"
import { Button } from "primereact/button"
import { LogError } from "../../../../../helpers/logger"

export default function ReferralEntityFormNameCheck({
  canEdit,
  onSelectProfile,
  children,
}) {
  const userCtx = useContext(UserContext)

  const [lastNameCheck, setLastNameCheck] = useState(undefined)
  const [triggerRecheck, setTriggerRecheck] = useState(false)
  const [notifyWhenDone, setNotifyWhenDone] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [checkResult, setCheckResult] = useState(undefined)
  const [showPartner, setShowPartner] = useState(undefined)

  const [nameField, nameFieldMeta] = useField("referralEntity.name")
  const [idField] = useField("referralEntity.id")

  const checkName = useCallback(async () => {
    if (!!lastNameCheck) {
      setIsLoading(true)
      try {
        const result = await refApiReferralEntityCheckName(
          userCtx.tokens.getMainToken(),
          idField.value,
          lastNameCheck,
        )

        if (notifyWhenDone) {
          if (!!result?.errorMessage) {
            userCtx.setNotificationError(
              `An error ocurred while looking for name matches: ${result.errorMessage}`,
            )
          } else if (result?.matches?.length === 0) {
            userCtx.setNotificationInformation(
              `No partners found with similar names`,
            )
          } else if (result?.matches?.length > 0) {
            userCtx.setNotificationInformation(
              `Found partners with similar names: ${result?.matches?.length}`,
            )
          }

          setNotifyWhenDone(false)
        }

        setIsLoading(false)
        setCheckResult(result)
      } catch (error) {
        userCtx.setNotificationError(
          `Unable to search for matching names: ${error.message}`,
        )
        LogError("Unable to search for matching names", error)
        setIsLoading(false)
        setCheckResult(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNameCheck, triggerRecheck])

  useEffect(() => {
    if (
      !!nameField.value &&
      nameField.value !== lastNameCheck &&
      nameField.value !== nameFieldMeta.initialValue
    ) {
      const scheduleNameCheck = setTimeout(
        () => setLastNameCheck(nameField.value),
        2 * 1000, //2seconds
      )

      return () => {
        clearTimeout(scheduleNameCheck)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameField.value, lastNameCheck])

  useEffect(() => {
    checkName()
  }, [checkName])

  return (
    <>
      <div className={styles.divPartnerNameContainer}>
        <div className={styles.divPartnerName}>{children}</div>
        {canEdit ? (
          <Button
            type="button"
            label={`Check`}
            className={styles.buttonPartnerNameCheck}
            icon={`pi ${
              lastNameCheck !== nameField.value
                ? "pi-exclamation-circle"
                : "pi-check"
            }`}
            loading={isLoading}
            onClick={() => {
              setNotifyWhenDone(true)

              if (lastNameCheck === nameField.value) {
                setTriggerRecheck(!triggerRecheck)
              } else {
                setLastNameCheck(nameField.value)
              }
            }}
            tooltip="Look for similar name in the Referral Partner database"
          />
        ) : null}
      </div>

      {showPartner ? (
        <ReferralEntityFormValidationDialog
          showDialog={!!showPartner}
          onHideDialog={() => {
            setShowPartner(undefined)
          }}
          validationPartnerProfile={showPartner}
          onSelectProfile={(e) => {
            setShowPartner(undefined)
            setCheckResult(undefined)
            onSelectProfile(e)
          }}
        />
      ) : null}

      {!!checkResult ? (
        <div
          className={`${styles.container} ${
            !!checkResult?.errorMessage
              ? styles.containerRed
              : !!checkResult?.matches?.length > 0
                ? styles.containerOrange
                : ""
          }`}
        >
          <div>{checkResult?.errorMessage}</div>
          {checkResult?.matches?.length > 0 ? (
            <>
              <div className={styles.result}>
                <span className="material-icons">{"warning"}</span>
                <div className={styles.resultText}>
                  Partners with similar names were found, click on the name to
                  see more information:
                </div>
              </div>
              <ul className={styles.list}>
                {checkResult?.matches.map((match) => {
                  return (
                    <li
                      onClick={() => {
                        setShowPartner(match)
                      }}
                    >
                      <div className={styles.listItem}>{match.name}</div>
                    </li>
                  )
                })}
              </ul>
            </>
          ) : (
            <div className={styles.result}>
              <span className={`material-icons ${styles.icon}`}>{"check"}</span>
              <div className={styles.resultText}>
                No matching partner names found
              </div>
            </div>
          )}
        </div>
      ) : null}
    </>
  )
}
